import { PageSection } from '@components/PageSection/PageSection';
import { FileUpload } from '@mui/icons-material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { Link as MuiLink } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useIntl } from 'react-intl';
import { Link, useParams } from 'react-router-dom';
import { PageSectionSkeleton } from '../../../../components/Skeletons/PageSectionSkeleton';
import { DomainEventType, RecentProjectEventsDocument, RecentProjectEventsQuery, RecentProjectEventsQueryVariables, SortEnumType } from '../../../../gql';
import { getTenantIdentifier } from '../../../../utils/getTenantIdentifier';
import { useGetMe } from '../../../../utils/useGetMe';
import { getFileNameWithoutExtension } from '../../../folders/utils/folderUtils';
import { RecentEventsTimeline } from '../RecentEvents/RecentEventsTimeline';
import { RecentEvent, eventTimelineMessageByTypeSecondPersonVerb, eventTimelineMessageByTypeThirdPersonVerb, iconByEventType } from '../RecentEvents/types';
import React from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { fetchData } from '../../../../gql/graphql-fetcher';


type LabeledLink = {
  label: string,
  url: string;
};

type ProjectEventNode = NonNullable<NonNullable<RecentProjectEventsQuery['projectEvents']>['edges']>[number]['node'];

const urlBuilders: Record<DomainEventType, (event: ProjectEventNode) => LabeledLink> = {
  DocumentSigned: e => ({ label: getFileNameWithoutExtension(e.document?.fileName ?? ''), url: `/${getTenantIdentifier()}/projects/${e.projectId}/documents/${e.document?.folderEntry?.folder?.path ?? ''}` }),
  FileDownloaded: e => ({ label: getFileNameWithoutExtension(e.document?.fileName ?? ''), url: `/${getTenantIdentifier()}/projects/${e.projectId}/documents/${e.document?.folderEntry?.folder?.path ?? ''}` }),
  FileUploaded: e => ({ label: getFileNameWithoutExtension(e.document?.fileName ?? ''), url: `/${getTenantIdentifier()}/projects/${e.projectId}/documents/${e.document?.folderEntry?.folder?.path ?? ''}` }),
  DocumentDeleted: e => ({ label: getFileNameWithoutExtension(e.document?.fileName ?? ''), url: `/${getTenantIdentifier()}/projects/${e.projectId}/documents` }),
  FormFilled: e => ({
    label: e.form?.formDefinition?.name ?? e.formSubmission?.formDefinition?.name ?? '',
    url: e.form ? `/${getTenantIdentifier()}/projects/${e.projectId}/form/${e.form?.id}/${e.formSubmissionId}`
      : `/${getTenantIdentifier()}/projects/${e.projectId}/forms/submissions/${e.formSubmissionId}`
  }),
  WorkflowActionSubmitted: e => ({ label: e.workflowAction?.name ?? '', url: `/${getTenantIdentifier()}/projects/${e.projectId}/workflows/instances/${e.workflowInstanceId}` }),
  MeetingRequested: e => ({ label: e.meeting?.name ?? '', url: `/${getTenantIdentifier()}/projects/${e.projectId}/meetings/requests/${e.meetingId}` }),
  MeetingAccepted: e => ({ label: e.meeting?.name ?? '', url: `/${getTenantIdentifier()}/projects/${e.projectId}/meetings/requests/${e.meetingId}` }),
  MeetingDeclined: e => ({ label: e.meeting?.name ?? '', url: `/${getTenantIdentifier()}/projects/${e.projectId}/meetings/requests/${e.meetingId}` }),
  DocumentSignatureRequestDenied: e => ({ label: getFileNameWithoutExtension(e.document?.fileName ?? ''), url: `/${getTenantIdentifier()}/projects/${e.projectId}/documents/${e.document?.folderEntry?.folder?.path ?? ''}` }),
  DocumentSignatureRequestExpired: e => ({ label: getFileNameWithoutExtension(e.document?.fileName ?? ''), url: `/${getTenantIdentifier()}/projects/${e.projectId}/documents/${e.document?.folderEntry?.folder?.path ?? ''}` }),
};

export const RecentActivitySection = () => {
  const { formatMessage } = useIntl();

  const { projectId } = useParams();

  const [selectedDate, setSelectedDate] = React.useState<dayjs.Dayjs | null>(null);

  const { me } = useGetMe();

  const pageSize = 25;

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,

  } = useInfiniteQuery({
    initialPageParam: undefined,
    queryKey: ['recentProjectEvents', projectId, selectedDate?.endOf('day').toISOString()],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      fetchData<RecentProjectEventsQuery, RecentProjectEventsQueryVariables>(
        RecentProjectEventsDocument,
        {
          projectId: Number(projectId),
          first: pageSize,
          after: pageParam,
          asOfDate: selectedDate?.endOf('day').toISOString(),
          order: [{ eventDate: SortEnumType.Desc }],
        }
      )(),
    getNextPageParam: (lastPage) =>
      lastPage.projectEvents?.pageInfo.hasNextPage
        ? lastPage.projectEvents?.pageInfo.endCursor ?? undefined
        : undefined,
  });

  const nodes: ProjectEventNode[] = (data?.pages ?? []).flatMap(p => p.projectEvents?.edges?.map(e => e.node) ?? []);

  const projectEventToRecentEvent = (projectEvent: ProjectEventNode): RecentEvent => {
    const link = urlBuilders[projectEvent.eventType](projectEvent);

    return ({
      eventDate: projectEvent.eventDate,
      eventText: projectEvent.eventTriggeredById === me?.id
        ? formatMessage(eventTimelineMessageByTypeSecondPersonVerb[projectEvent.eventType], {
          link: <MuiLink key={`event-link-${projectEvent.id}`} display={'inline-block'} sx={{ textTransform: 'lowercase' }} component={Link} to={link.url}>{link.label}</MuiLink>
        })
        : formatMessage(eventTimelineMessageByTypeThirdPersonVerb[projectEvent.eventType], {
          user: <b>{projectEvent.eventTriggeredBy?.fullName ?? ''}</b>,
          link: <MuiLink key={`event-link-${projectEvent.id}`} display={'inline-block'} sx={{ textTransform: 'lowercase' }} component={Link} to={link.url}>{link.label}</MuiLink>
        }),
      icon: iconByEventType[projectEvent.eventType]
    });
  };

  function groupUploadsByUserFolderAndHour(dailyEventsByType: ProjectEventNode[]) {
    return _(dailyEventsByType)
      .groupBy(p => dayjs(p.eventDate).startOf('hour').toISOString() + (p.document?.folderEntry?.folder?.id ?? '') + (p.eventTriggeredBy?.fullName ?? ''))
      .map((events): RecentEvent => {
        const folder = events[0].document?.folderEntry?.folder;
        const folderUrl = `/${getTenantIdentifier()}/projects/${projectId}/documents/${folder?.path ?? ''}`;
        const folderName = folder?.name ?? formatMessage({ id: 'Documents' });
        return events.length > 1 ?
          ({
            eventDate: dayjs(events[0].eventDate).startOf('hour').toISOString(),
            eventText: events[0].eventTriggeredById === me?.id
              ? formatMessage({ id: 'You uploaded {count} files in {link}' }, {
                count: events.length,
                link: <MuiLink display={'inline-block'} sx={{ textTransform: 'lowercase' }} component={Link} to={folderUrl}>{folderName}</MuiLink>
              })
              : formatMessage({ id: '{user} uploaded {count} files in {link}' }, {
                user: <b>{events[0].eventTriggeredBy?.fullName}</b>,
                count: events.length,
                link: <MuiLink display={'inline-block'} sx={{ textTransform: 'lowercase' }} component={Link} to={folderUrl}>{folderName}</MuiLink>
              }),

            icon: <FileUpload color='action' />,
          })
          :
          projectEventToRecentEvent(events[0]);
      })
      .value();
  }

  const recentEvents = _(nodes)
    .groupBy(p => dayjs(p.eventDate).startOf('day'))
    .flatMap((events) => _(events)
      .groupBy(p => p.eventType)
      .flatMap((dailyEventsByType) => {
        switch (dailyEventsByType[0].eventType) {
          case DomainEventType.FileUploaded:
            return groupUploadsByUserFolderAndHour(dailyEventsByType);
          default:
            return dailyEventsByType.map(projectEventToRecentEvent);
        }
      })
      .orderBy(p => p.eventDate, 'desc')
      .value())
    .value();

  if (isLoading) {
    return (<PageSectionSkeleton icon={<NotificationsIcon />} title={formatMessage({ id: 'Recent Activity' })} />);
  }

  const today = dayjs();

  return <>
    <PageSection
      icon={<NotificationsIcon />}
      title={formatMessage({ id: 'Activity' })}
      emptyState={formatMessage({ id: 'There are no recent activities in the project.' })}
      actionButton={<DatePicker
        label={formatMessage({ id: 'Up to date' })}
        value={selectedDate}
        disableFuture
        maxDate={today}
        onChange={(d) => { setSelectedDate(d); }}
        slotProps={{ textField: { size: 'small' } }}
      />
      }
    >



      {recentEvents.length > 0 && (
        <RecentEventsTimeline recentEvents={recentEvents} fetchNextPage={fetchNextPage} hasNextPage={!!hasNextPage} isFetchingNextPage={isFetchingNextPage} />
      )}



    </PageSection>
  </>;
};

